<template>
  <div class="relative flex-1 @container flex">
    <ChannelQuiz
      v-if="quizCardState.currentQuestion"
      :quiz="quizCardState.quiz! as unknown as ChannelQuizData"
      :question="quizCardState.currentQuestion as ChannelQuizQuestionData"
      :done-questions="quizCardState.currentQuestionNumber!"
      :total-questions="quizCardState.questionsCount!"
      class="flex-1"
      @delete="emit('delete')"
    >
      <template #main>
        <template v-if="showOptionsInline">
          <ChannelQuizOption
            v-for="option in quizCardState.currentQuestion.options"
            :key="option.id"
            :is-correct="
              quizCardState.currentQuestion.status === QuestionStatus.Done &&
              option.is_correct === true
            "
            :read-only="
              props.disabled ||
              quizCardState.currentQuestion.status === QuestionStatus.Done ||
              !!quizCardState.currentQuestion.selected_option_id
            "
            :is-poll="quizCardState.isPoll"
            :enable-participation-count="enableParticipationCount"
            :question-participation-count="questionParticipationTotalCount ?? 0"
            :question-option-participation-count="
              questionParticipationCountMap?.[option.id] ?? 0
            "
            :is-submitted="
              quizCardState.selectedOptionId === option.id ||
              quizCardState.currentQuestion.selected_option_id === option.id
            "
            :created-by-id="
              quizCardState.quiz?.created_by_id ??
              quizCardState.quiz?.created_by?.id
            "
            :option="option"
            @click="onChannelQuizOptionClick(option.id)"
          />
        </template>

        <template v-else>
          <div class="relative">
            <div
              class="flex flex-col gap-1.5 [-webkit-mask-image:linear-gradient(to_bottom,black_45%,rgba(0,0,0,0.4)_72%,transparent_98%)] [mask-image:linear-gradient(to_bottom,black_45%,rgba(0,0,0,0.4)_72%,transparent_98%)]"
            >
              <ChannelQuizOption
                v-for="option in previewOptions"
                :key="option.id"
                :option="option"
                :is-submitted="
                  option.id === quizCardState.currentQuestion.selected_option_id
                "
                :is-correct="
                  quizCardState.currentQuestion.status ===
                    QuestionStatus.Done && option.is_correct === true
                "
                :read-only="true"
                :is-poll="quizCardState.isPoll"
                :enable-participation-count="
                  enableParticipationCount &&
                  option.id === quizCardState.currentQuestion.selected_option_id
                "
                :question-participation-count="
                  questionParticipationTotalCount ?? 0
                "
                :question-option-participation-count="
                  questionParticipationCountMap?.[option.id] ?? 0
                "
                :created-by-id="
                  quizCardState.quiz?.created_by_id ??
                  quizCardState.quiz?.created_by?.id
                "
                @click="isAnswerSelectModalOpen = true"
              />
            </div>

            <div
              class="absolute inset-x-0 bottom-1 z-10 flex flex-col items-center gap-1.5"
            >
              <div
                class="rounded-full border border-brand-quaternary-40 bg-brand-bgdivider-20 backdrop-blur-lg px-3 py-1 font-sans text-xs font-bold text-basics-secondary-80"
              >
                {{
                  $t("channel.contests.quiz.selectAnswer.moreAnswers", {
                    count: hiddenOptionCount,
                  })
                }}
              </div>

              <ContestButton
                :variant="canAnswerCurrentQuestion ? 'primary' : 'subtle'"
                class="!h-11 !flex-none !px-8"
                @click="isAnswerSelectModalOpen = true"
              >
                {{
                  $t(
                    canAnswerCurrentQuestion
                      ? "channel.contests.quiz.actions.startQuiz"
                      : "channel.contests.quiz.actions.seeAnswers",
                  )
                }}
              </ContestButton>
            </div>
          </div>

          <QuizAnswerSelectModal
            v-model="isAnswerSelectModalOpen"
            :disabled="props.disabled"
            :enable-participation-count="enableParticipationCount"
            :question-participation-total-count="
              questionParticipationTotalCount
            "
            :question-participation-count-map="questionParticipationCountMap"
            @answer-submitted="emit('answerSubmitted')"
          />
        </template>
      </template>
    </ChannelQuiz>
  </div>
</template>

<script setup lang="ts">
import {
  QuestionStatus,
  type GetQuizzesForChannelQuery,
} from "@/generated/graphql";
import type { ElementOfArray } from "@/utils/types";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";

type ChannelQuizData = ElementOfArray<
  GetQuizzesForChannelQuery["quizzesForChannel"]
>;
type ChannelQuizQuestionData = ElementOfArray<
  ChannelQuizData["quiz_questions"]
>;
import { ChannelPermission } from "@f3/shared/permissions";
import { computed, ref } from "vue";
import ChannelQuizOption from "@/components/contest/channel-quiz-options.vue";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import { useChannelStore } from "@/stores/channel.store";
import { useQuizCardState } from "../quiz-card/quiz-card-state";
import ChannelQuiz from "../channel-quiz.vue";
import ContestButton from "../contest-button.vue";
import QuizAnswerSelectModal from "./quiz-answer-select-modal.vue";
import { MAX_INLINE_QUIZ_QUESTION_OPTIONS } from "@/constants/quiz";

const _channelStore = useChannelStore();

const emit = defineEmits<{
  delete: [];
  answerSubmitted: [];
}>();

const props = defineProps<{
  loading: boolean;
  disabled: boolean;
  enableParticipationCount: boolean;
  questionParticipationTotalCount: number | undefined | null;
  questionParticipationCountMap: Record<string, number> | undefined | null;
}>();

const quizCardState = useQuizCardState();

const showOptionsInline = computed(
  () =>
    (quizCardState.currentQuestion?.options?.length ?? 0) <=
    MAX_INLINE_QUIZ_QUESTION_OPTIONS,
);

const canAnswerCurrentQuestion = computed(
  () =>
    !props.disabled &&
    quizCardState.currentQuestion?.status !== QuestionStatus.Done &&
    !quizCardState.currentQuestion?.selected_option_id,
);

const isAnswerSelectModalOpen = ref(false);

const submittedOption = computed(() => {
  const question = quizCardState.currentQuestion;
  if (!question?.selected_option_id) return null;
  return (
    question.options?.find(
      (option) => option.id === question.selected_option_id,
    ) ?? null
  );
});

const UNANSWERED_PREVIEW_OPTION_COUNT = 5;
const ANSWERED_TEASER_OPTION_COUNT = 2;

const previewOptions = computed(() => {
  const options = quizCardState.currentQuestion?.options ?? [];
  const submitted = submittedOption.value;
  if (!submitted) return options.slice(0, UNANSWERED_PREVIEW_OPTION_COUNT);

  const teasers = options
    .filter((option) => option.id !== submitted.id)
    .slice(0, ANSWERED_TEASER_OPTION_COUNT);
  return [submitted, ...teasers];
});

const hiddenOptionCount = computed(() => {
  const total = quizCardState.currentQuestion?.options?.length ?? 0;
  return Math.max(total - previewOptions.value.length, 0);
});

const { handler: selectOption } = useGatingForAction(
  ChannelPermission.interactInContests,
  (option_id: string) => {
    if (!props.disabled) quizCardState.selectedOptionId = option_id;
  },
  {
    gatingExperience: {
      experienceId: quizCardState.quiz?.id,
      experienceType: ExperienceType.Quiz,
      experienceAction: ExperienceAction.SELECT_OPTION,
      experienceCreatorId: quizCardState.quiz?.created_by?.id ?? undefined,
      experienceCreatorUsername:
        quizCardState.quiz?.created_by?.username ?? undefined,
      experienceCommunityIds: quizCardState.quiz?.quiz_communities
        ?.map((c) => c.community?.id)
        .filter((id): id is string => id != null),
      experienceCommunityNames: quizCardState.quiz?.quiz_communities
        ?.map((c) => c.community?.name)
        .filter((name): name is string => name != null),
    },
  },
);

function onChannelQuizOptionClick(optionId: string) {
  if (
    props.disabled ||
    quizCardState.currentQuestion?.status === QuestionStatus.Done ||
    quizCardState.currentQuestion?.selected_option_id
  )
    return;

  selectOption(optionId);
}
</script>
